import { VStack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { ErrorListResponse, instanceOfErrorListResponse, Service } from '@genial/services';
import { InteractionChannelAuditRequest } from '@genial/services/dist/models';
import React from 'react';

import { useSendingChannels } from '../../../../../../../../hooks/useSendingChannels';
import { useToastCreator } from '../../../../../../../../hooks/useToastCreator';
import { useEquityAssetController } from '../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/Equity';
import {
  orderResumePdfTemplate,
  parsePDFDataExport,
  setupPDF,
} from '../../../../../../../Resume/utils';
import { CardHeader } from '../../components/CardHeader';
import { ConfirmationButton } from '../../components/ConfirmationButton';
import { SimplifiedApplicationMissingTerm } from '../../components/SimplifiedApplicationMissingTerm';
import { TicketApplicationInProgress } from '../../components/TicketApplicationInProgress';
import { TicketApplicationSuccessDetails } from '../../components/TicketApplicationSuccessDetails';
import { defaultTicketApplicationBaseInfo } from '../../components/TicketApplicationSuccessDetails/constants';
import { TicketDetails } from '../../components/TicketDetails';
import {
  defaultErrorMessageApplication,
  screenName,
  screenNameSimplifiedFlow,
} from '../../constants';
import { CustomerApplicationModalProductProps } from '../../types';

import { EquityInvestCard } from './components/EquityInvestCard';
import {
  getEquitySuccessInfos,
  ResponsesEquityOrderCreateResponseWithOrderType,
} from './constants';

const serializeEquityOrderTypeForApi = (orderType?: 'Limit' | 'Market'): 'Limit' | 'Market' =>
  orderType === 'Market' ? 'Market' : 'Limit';

export interface EquityApplicationProps extends CustomerApplicationModalProductProps<'equity'> {
  onClose: () => void;
  handleFollowTicket: () => void;
}

export const EquityApplication: React.FC<EquityApplicationProps> = ({
  offer,
  onClose,
  customerId,
  productType,
  accountNumber,
  handleFollowTicket,
  isSimplifiedApplication,
  isSimplifiedApplicationFlagMatched,
  showSendingChannels,
  clientHasTerm,
  isClientTermLoading,
}) => {
  const { showToastFromError, showToast } = useToastCreator('equityApplicationInCustomer');
  const [sendingOrder, setSendingOrder] = React.useState(false);
  const [interactionChannelAudit, setInteractionChannelAudit] =
    React.useState<InteractionChannelAuditRequest | null>();
  const [success, setSuccess] = React.useState(false);
  const [successInfos, setSuccessInfos] = React.useState(defaultTicketApplicationBaseInfo);
  const [ticketSuccessData, setTicketSuccessData] =
    React.useState<ResponsesEquityOrderCreateResponseWithOrderType>();

  const offerController = useEquityAssetController(offer);
  const sendingChannelsController = useSendingChannels(customerId);

  const buttonDisabled =
    !!offerController.totalPriceIsInvalid ||
    (!sendingChannelsController.sendingChannelsSelected.length && !isSimplifiedApplication) ||
    interactionChannelAudit === undefined;

  const handleExportNewOrder = () => {
    if (!ticketSuccessData) {
      return;
    }
    const data = parsePDFDataExport(ticketSuccessData as any);
    setupPDF(orderResumePdfTemplate(data, 'Equity'), `ordem-${successInfos.orderHash}.pdf`);
  };

  const handleConfirmOrder = () => {
    setSendingOrder(true);
    Service.Equities.V1.postOrdersBuy({
      screenName: isSimplifiedApplication ? screenNameSimplifiedFlow : screenName,
      equityOrderCreateRequest: {
        accountNumber,
        orderPrice: offerController.priceItem as number,
        quantity: offerController.amountItem as number,
        productId: offer.productIdNormalized,
        orderType: serializeEquityOrderTypeForApi(offerController.orderType),
        notificationChannels: isSimplifiedApplication
          ? null
          : sendingChannelsController.sendingChannelsSelected,
        orderName: 'Renda Variavel',
        interactionChannelAudit: interactionChannelAudit!,
      },
    })
      .then(({ data }) => {
        setSuccess(true);
        setTicketSuccessData({
          ...data,
          orderType: serializeEquityOrderTypeForApi(offerController.orderType),
        });
        setSuccessInfos(
          getEquitySuccessInfos(
            data.offer,
            data.order,
            showSendingChannels,
            isSimplifiedApplicationFlagMatched,
            offerController.orderType
          )
        );
      })
      .catch((error) => {
        if (instanceOfErrorListResponse(error as object)) {
          (error as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }

        showToast(...defaultErrorMessageApplication);
      })
      .finally(() => setSendingOrder(false));
  };

  if (success) {
    return (
      <TicketApplicationSuccessDetails
        {...successInfos}
        onClose={onClose}
        onFollow={handleFollowTicket}
        onExportPdf={handleExportNewOrder}
      />
    );
  }

  if (sendingOrder) {
    return <TicketApplicationInProgress />;
  }

  return (
    <VStack space={Spacing['stack.xxxs']}>
      <CardHeader onClose={onClose} />

      <EquityInvestCard offer={offer} offerController={offerController} />

      <SimplifiedApplicationMissingTerm
        isClientTermAvailable={clientHasTerm}
        isClientTermLoading={isClientTermLoading}
        isSimplifiedApplicationFlagMatched={isSimplifiedApplicationFlagMatched}
      />

      <TicketDetails
        productTypeId={productType}
        sendingChannelsController={sendingChannelsController}
        setInteractionChannelAudit={setInteractionChannelAudit}
        interactionChannelAudit={interactionChannelAudit}
        showSendingChannels={showSendingChannels}
        isSimplifiedApplicationFlagMatched={isSimplifiedApplicationFlagMatched}
      />

      <ConfirmationButton onConfirm={handleConfirmOrder} disabled={buttonDisabled} />
    </VStack>
  );
};
